/* eslint-disable no-new-func */
// React
import React, { useState, useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";

// Libraries
import PropTypes from "prop-types";
import { Form, Button, Label, Icon } from "semantic-ui-react";
import { map, includes, get, isEmpty, isNaN, toLower, find, replace, isArray, filter, deburr, trim } from "lodash";
import TextareaAutosize from "react-textarea-autosize";

// Components
import Input from "@Components/Input";
import DropDownColor from "@Components/DropDownColor";
import MapAddressSelector from "@Components/MapAddressSelector";
import FileManager from "@Components/FileManager";

// Redux
import { connect } from "react-redux";
import { sendMessage } from "@Actions/chats";
import downloadFile from "@Redux/Actions/downloads";

// chat
import { messageBuilder, getTimeLabel } from "@Utils/functionality";

// Utils
import { dropdownFormat } from "@Utils/formats";

// Styles
import styles from "./styles.module.css";

const Field = ({
	fieldData = {},
	fieldCard = {},
	companyFieldsInputHandler = () => {},
	isEditable = false,
	dispatch,
	reply,
	userName,
	chats,
	noSendQuestion,
	questionsLanguage,
	groups,
	groupsToken,
	token,
	disabledStyle,
	companyName,
}) => {
	const [openMapAddressSelector, setOpenMapAddressSelector] = useState(false);
	const separatorSymbol = "§";
	const componentRef = useRef(null);

	const handlePrint = useReactToPrint({
		content: () => {
			return componentRef.current;
		},
	});

	// getField
	const getFieldByLabel = (fieldName) => {
		const field = fieldCard.items.find((item) => {
			return item.label.toLowerCase() === fieldName.toLowerCase();
		});
		return field;
	};

	const hasValue = () => {
		const value = get(fieldData, "value", "");
		if (!isEmpty(value) && typeof value === "string") {
			const [firstValue] = value.split(separatorSymbol);
			return !isEmpty(firstValue);
		}

		if (typeof value === "boolean") {
			return value;
		}

		return !isEmpty(value);
	};

	/* input handler */
	const handleInput = (e, { name, value }) => {
		companyFieldsInputHandler({ name, value, fieldCard, fieldData });
	};

	/* textArea handler */
	const handleTextArea = (event) => {
		const fieldLabel = get(fieldData, "label", "");
		companyFieldsInputHandler({ name: fieldLabel, value: event.target.value, fieldCard, fieldData });
	};

	/* input default handler */
	const handleInputDefault = (event, { name, value }) => {
		const fieldLabel = get(fieldData, "label", "");
		const compositeValue = get(fieldData, "value", "");
		const [primaryPart = "", secondaryPart = ""] = compositeValue.split(separatorSymbol);

		let updatedValue;

		if (name === "dropDownSeparateValue") {
			updatedValue = [primaryPart, value].join(separatorSymbol);
		} else {
			updatedValue = [value, secondaryPart].join(separatorSymbol);
		}

		companyFieldsInputHandler({ name: fieldLabel, value: updatedValue, fieldCard, fieldData });
	};

	/* check handler */
	const handleCheck = () => {
		const { label, value } = fieldData;
		companyFieldsInputHandler({ name: label, value: !value, fieldCard, fieldData });
	};

	/* address handler */
	const showAddressAddHandler = () => {
		setOpenMapAddressSelector(!openMapAddressSelector);
	};

	const addressSelectHandler = (currentAddressData) => {
		const { label } = fieldData;
		let addressData = currentAddressData;
		let title = get(addressData, "title", "");
		if (!isEmpty(get(addressData, "title", ""))) {
			title = get(addressData, "title", "");
		} else if (!isEmpty(get(addressData, "sector", ""))) {
			title = get(addressData, "sector", "");
		} else if (!isEmpty(get(addressData, "city", ""))) {
			title = get(addressData, "city", "");
		}
		addressData = { ...addressData, title };
		companyFieldsInputHandler({ name: label, value: addressData, fieldCard, fieldData });
		showAddressAddHandler();
	};

	const handlerOpenDirection = () => {
		setOpenMapAddressSelector(!openMapAddressSelector);
	};

	const handlerRemoveDirection = () => {
		const { label } = fieldData;
		companyFieldsInputHandler({ name: label, value: {}, fieldCard, fieldData });
	};

	/* label input handler */
	const handleLabelInput = (value) => {
		const { label } = fieldData;
		companyFieldsInputHandler({ name: label, value, fieldCard, fieldData });
	};

	/* image handler */
	const imageHandler = (images) => {
		const { label } = fieldData;
		companyFieldsInputHandler({ name: label, value: images, fieldCard, fieldData });
	};

	/* formula handler */
	const getRecommendation = ({ calculatedValue }) => {
		const { label } = fieldData;
		if (toLower(label) !== "imc") {
			return "";
		}

		const bariatricField = getFieldByLabel("Bariátrica Previa");
		let bariatricValue = false;

		if (!calculatedValue) {
			return "";
		}

		if (bariatricField) {
			bariatricValue = !!get(bariatricField, "value", "");
		}

		if (bariatricValue) {
			return "Remanga,revision de bypass";
		}

		if (calculatedValue <= 32.4) {
			return "Balón";
		}

		if (calculatedValue <= 35.9) {
			return "Manga Endoscopica";
		}

		return "Manga Laparoscopica";
	};

	const unitConversion = (value, fromUnit, toUnit) => {
		const conversionRates = {
			"kilogramos-libras": 2.20462,
			"libras-kilogramos": 0.453592,
			"metros/cm-in": 0.393701,
			"in-metros/cm": 2.54,
			"cm-pies/pulg": 0.0328084,
			"pies/pulg-cm": 30.48,
			"cm-metros/cm": 0.01,
			"metros/cm-cm": 100,
			"metros/cm-pulg": 39.3701,
			"pulg-metros/cm": 0.0254,
			"metros/cm-pies/pulg": 3.370079,
		};

		if (fromUnit.toLowerCase() === "pies/pulg" && toUnit.toLowerCase() === "metros/cm") {
			const parts = value.toString().split(".");
			if (parts.length === 2) {
				const feet = parseInt(parts[0], 10);
				const inches = parseInt(parts[1], 10);
				if (isNaN(feet) || isNaN(inches)) {
					throw new Error(`Valor inválido para conversión de Pies/Pulg: ${value}`);
				}
				const totalInches = feet * 12 + inches;
				return totalInches * 0.0254;
			}

			const feet = parseFloat(value);
			if (isNaN(feet)) {
				throw new Error(`Valor inválido para conversión de Pies/Pulg: ${value}`);
			}
			return feet * 0.3048;
		}

		const key = `${fromUnit.toLowerCase()}-${toUnit.toLowerCase()}`;
		const factor = conversionRates[key];
		if (factor) {
			return value * factor;
		}
		throw new Error(`Conversion from ${fromUnit} to ${toUnit} is not supported.`);
	};

	const getNumericValueFromVariable = (variable) => {
		let numValue = null;
		let unit = "";
		if (fieldCard.items && Array.isArray(fieldCard.items)) {
			const fieldItem = fieldCard.items.find((item) => {
				return item.label.toLowerCase() === variable.toLowerCase();
			});
			if (fieldItem && fieldItem.value && String(fieldItem.value).trim() !== "") {
				const parts = fieldItem.value.split(separatorSymbol);
				const primaryValue = parts[0];
				unit = parts[1] ? parts[1].trim().toLowerCase() : "";
				if (unit === "pies/pulg") {
					numValue = primaryValue.toString();
				} else {
					numValue = parseFloat(primaryValue);
				}
			}
		} else if (fieldCard[variable] !== undefined && String(fieldCard[variable]).trim() !== "") {
			numValue = parseFloat(fieldCard[variable]);
		}
		return { numValue, unit };
	};

	/* options handler */
	const getOptions = () => {
		let { options } = fieldData;

		if (typeof options === "string") {
			options = JSON.parse(replace(options, /'/g, "\""));
		}

		if (!isArray(options) || isEmpty(options)) {
			return [];
		}

		return map(options, (option, index) => {
			if (typeof option === "string") {
				return { key: option, value: option, text: option };
			}

			return {
				key: option.id || index,
				value: option.name,
				text: option.name,
			};
		});
	};

	// field types renders
	const enumField = ({ label, isUpdateable, isMultiple, order, value, placeholder, isRequired, disabled }) => {
		let dropdownValue = "";
		if (isMultiple) {
			if (Array.isArray(value)) {
				dropdownValue = value;
			} else if (value) {
				dropdownValue = [value];
			} else {
				dropdownValue = [];
			}
		} else {
			dropdownValue = !isEmpty(value) ? value : "";
		}

		return (
			<div className={`${styles.dropDownContainer} ${!isUpdateable ? styles.fieldContainerDisabled : ""}`} style={{ marginBottom: "1em" }}>
				<Form.Dropdown
					key={`field-${label}-${order}`}
					multiple={isMultiple}
					className={`${!isUpdateable ? styles.inputNoEditable : styles.input} ${disabledStyle ? styles.disabledTextAreaStyle : ""}`}
					disabled={disabled}
					name={label}
					search
					label={label}
					required={isRequired}
					placeholder={placeholder || ""}
					value={dropdownValue}
					onChange={handleInput}
					fluid
					selection
					options={getOptions()}
				/>
			</div>
		);
	};

	const groupField = ({ label, isUpdateable, isMultiple, order, value, placeholder, isRequired, disabled, options }) => {
		let dropdownValue = "";
		if (isMultiple) {
			if (Array.isArray(value)) {
				dropdownValue = value;
			} else if (value) {
				dropdownValue = [value];
			} else {
				dropdownValue = [];
			}
		} else {
			dropdownValue = !isEmpty(value) ? value : "";
		}

		let finalGroups = token ? groupsToken : groups;

		const normalizeString = (str) => {
			return deburr(toLower(trim(str)));
		};

		if (isArray(options) && !isEmpty(options)) {
			const filterNames = map(options, (opt) => {
				return typeof opt === "string" ? normalizeString(opt) : normalizeString(get(opt, "name", ""));
			});

			finalGroups = filter(finalGroups, (group) => {
				const typeName = normalizeString(get(group, "type.name", ""));
				return includes(filterNames, typeName);
			});
		} else if (typeof options === "string" && !isEmpty(options)) {
			finalGroups = filter(finalGroups, (group) => {
				const typeName = normalizeString(get(group, "type.name", ""));
				return typeName === normalizeString(options);
			});
		}

		const groupOptions = dropdownFormat(finalGroups, "name", "name");

		return (
			<div className={` ${!isUpdateable ? styles.fieldContainerDisabled : ""}`}>
				<Form.Dropdown
					key={`field-${label}-${order}`}
					multiple={isMultiple}
					className={`${!isUpdateable ? styles.inputNoEditable : styles.input} ${disabledStyle ? styles.disabledStyle : ""}`}
					disabled={disabled}
					name={label}
					search
					label={label}
					required={isRequired}
					placeholder={placeholder || ""}
					value={dropdownValue}
					onChange={handleInput}
					fluid
					selection
					options={groupOptions}
					upward={false}
					clearable
				/>
			</div>
		);
	};

	const labelField = ({ label, isUpdateable, options, order, value, placeholder, isRequired, disabled }) => {
		let dropdownValue = "";
		if (!isEmpty(value)) {
			dropdownValue = value;
		}

		const currentOptions = map(options, (option) => {
			return {
				name: get(option, "name"),
				color: get(option, "color"),
				label: get(option, "name"),
			};
		});

		return (
			<div className={`${disabled ? styles.disabledStyle : ""} ${!isUpdateable ? styles.fieldContainerDisabled : ""}`}>
				<label className={styles.customLabel}>
					{label}
					{isRequired && <span className={styles.required}> *</span>}
				</label>
				<DropDownColor
					className={`${!isUpdateable ? styles.inputNoEditable : styles.input} ${disabledStyle ? styles.disabledStyle : ""}`}
					key={`field-${label}-${order}`}
					disabled={disabled}
					name={label}
					label=""
					required={isRequired}
					placeholder={placeholder || ""}
					options={currentOptions}
					selectedColors={dropdownValue}
					setSelectedColors={handleLabelInput}
					direction="backward"
				/>
			</div>
		);
	};

	const noteField = ({ label, isUpdateable, order, value, placeholder, isRequired, disabled }) => {
		return (
			<div className={`${!isUpdateable ? styles.fieldContainerDisabled : ""}`}>
				<label className={styles.customLabel}>
					{label}
					{isRequired && <span className={styles.required}> *</span>}
				</label>

				<TextareaAutosize
					key={`field-${label}-${order}`}
					rows={2}
					name={label}
					className={`${!isUpdateable ? styles.textareaNoEditable : styles.textarea} ${disabledStyle ? styles.disabledTextAreaStyle : ""}`}
					placeholder={placeholder || ""}
					disabled={disabled}
					value={value}
					onChange={handleTextArea}
				/>
			</div>
		);
	};

	const boolField = ({ label, isUpdateable, order, value, placeholder, icon, disabled, isRequired }) => {
		return (
			<div style={{ display: "flex" }} className={`${!isUpdateable ? styles.fieldContainerDisabled : ""}`}>
				<label className={styles.customLabel}>
					{label}
					{isRequired && <span className={styles.required}>*</span>}
				</label>
				<Form.Checkbox
					toggle
					key={`field-${label}-${order}`}
					className={`${!isUpdateable ? styles.inputNoEditable : styles.checkbox} ${disabledStyle ? styles.disabledStyle : ""}`}
					disabled={disabled}
					name={label}
					/* 		required={isRequired} */
					placeholder={placeholder || ""}
					icon={icon}
					checked={value}
					onClick={handleCheck}
				/>
				<span style={{ marginLeft: "0.5em" }}>{value ? "Si" : "No"}</span>
			</div>
		);
	};

	const directionField = ({ label, isUpdateable, order, value, isRequired, disabled }) => {
		const currentValue = isEmpty(value) || typeof value !== "object" ? {} : value;
		return (
			<div className={`${!isUpdateable ? styles.fieldContainerDisabled : ""}`}>
				<div className={styles.addressLabelContainer}>
					<div>
						<span className={styles.customInputLabel}>
							{label} {isRequired && <span className={styles.required}> *</span>}
						</span>
						{isUpdateable && !disabled && (
							<Button
								icon={isEmpty(get(currentValue, "formattedAddress", "")) ? "plus" : "edit"}
								className={styles.addressEditButton}
								onClick={handlerOpenDirection}
							/>
						)}
					</div>
					{isUpdateable && !disabled && !isEmpty(get(currentValue, "formattedAddress", "")) && (
						<Button icon="trash" className={styles.addressRemoveButton} onClick={handlerRemoveDirection} />
					)}
				</div>
				<TextareaAutosize
					key={`field-${label}-${order}`}
					rows={2}
					name={label}
					className={styles.textareaNoEditable}
					placeholder={`Presionar botón "+" para agregar dirección`}
					disabled
					value={get(currentValue, "formattedAddress", "")}
					onChange={handleTextArea}
				/>
				{openMapAddressSelector && (
					<MapAddressSelector
						onSelect={addressSelectHandler}
						onClose={() => {
							setOpenMapAddressSelector(false);
						}}
						defaultData={currentValue}
					/>
				)}
			</div>
		);
	};

	const convertToImageSrc = async (images) => {
		const newAttachments = await Promise.all(
			map(images, async (item) => {
				const isUrl = String(item).startsWith("http");
				const isBase64 = String(item).startsWith("data:image");
				const isRawBase64 = !isUrl && !isBase64 && !String(item).includes("/");

				let url = item;
				let isImage = null;

				if (isRawBase64) {
					url = `data:image/jpeg;base64,${item}`;
					isImage = true;
				} else if (!isUrl && !isBase64) {
					url = await dispatch(downloadFile({ uri: item }));
					isImage = String(item).match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
				} else {
					isImage = String(item).match(/image/i);
				}

				return {
					link: url,
					type: isEmpty(isImage) ? "pdf" : "image",
				};
			})
		);

		return newAttachments;
	};

	const imageField = ({ label, value, disabled, isRequired }) => {
		const [currentImageSrc, setCurrentImageSrc] = useState([]);

		useEffect(() => {
			const loadImage = async () => {
				if (!isEmpty(value)) {
					const result = await convertToImageSrc(value);
					setCurrentImageSrc(result);
				}
			};
			loadImage();
		}, [value]);

		let currentValue = value;
		if (isEmpty(value)) {
			currentValue = [];
		}

		return (
			<div className={styles.imageFieldContainer}>
				<label className={`${styles.customLabel} ${styles.noPrintLabel}`}>
					{label}
					{isRequired && <span className={styles.required}>*</span>}
					<Icon name="print" className={styles.icon} onClick={handlePrint} />
				</label>

				<div className={styles.imageContainer}>
					{isEmpty(value) && disabled ? (
						<div style={{ position: "relative", display: "inline-block" }} className={styles.iconImage}>
							<Icon name="image outline" size="huge" />
							<Icon name="close" size="big" color="red" style={{ position: "absolute", top: 0, right: 0 }} />
						</div>
					) : (
						<>
							<div style={{ display: "none" }}>
								<div ref={componentRef} className={styles.printableContainer}>
									<h1 className={styles.printTitle}>{companyName}</h1>

									<div className={styles.printMetadata}>
										<p>
											<span>Usuario:</span> {userName}
										</p>
										<p>
											<span>Fecha:</span> {getTimeLabel(new Date(), "DD/MM/YYYY hh:mm a")}
										</p>
									</div>

									{!isEmpty(currentImageSrc) && <img src={currentImageSrc[0]?.link} alt="imagen" />}
								</div>
							</div>

							<div className={styles.image}>
								<FileManager images={currentValue} setImages={imageHandler} disabled={disabled} />
							</div>
						</>
					)}
				</div>
			</div>
		);
	};

	const formulaField = ({ label, isUpdateable, order, value, placeholder, isRequired, icon, disabled, formula, options }) => {
		let calculatedValue = value;
		if (formula) {
			const variables = [...new Set(formula.match(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g))];
			try {
				const dynamicFunction = new Function(...variables, `return ${formula};`);
				const values = variables.map((v) => {
					const { numValue, unit: currentUnit } = getNumericValueFromVariable(v, fieldCard, "§");
					let finalValue = numValue;

					if (finalValue !== null && options && options.length > 0) {
						const conversionOption = options.find((opt) => {
							return opt.name.toLowerCase() === v.toLowerCase();
						});
						if (conversionOption) {
							const desiredUnit = conversionOption.unit.toLowerCase();
							if (currentUnit && currentUnit !== desiredUnit) {
								finalValue = unitConversion(finalValue, currentUnit, desiredUnit);
							}
						}
					}

					return finalValue;
				});
				const allValuesProvided = values.every((val) => {
					return val !== null && val !== undefined;
				});
				if (allValuesProvided) {
					calculatedValue = dynamicFunction(...values);
				} else {
					calculatedValue = "";
				}
			} catch (error) {
				calculatedValue = "Error";
			}
		}

		calculatedValue = isNaN(calculatedValue) ? "" : parseFloat(Number(calculatedValue).toFixed(2));

		if (value !== (calculatedValue || "")) {
			companyFieldsInputHandler({ name: label, value: calculatedValue || "", fieldCard, fieldData });
		}

		const recommendation = getRecommendation({ calculatedValue });

		return (
			<>
				<div className={`${styles.defaultInputContainer} ${isUpdateable ? "" : styles.fieldContainerDisabled}`}>
					<Input
						key={`field-${label}-${order}`}
						className={`${!isUpdateable ? styles.inputNoEditable : styles.input} ${disabledStyle ? styles.disabledStyle : ""}`}
						disabled={disabled}
						name={label}
						required={isRequired}
						label={label}
						placeholder={placeholder || ""}
						icon={icon}
						value={calculatedValue || ""}
						onChange={handleInputDefault}
						fluid
					/>
				</div>
				{!isEmpty(recommendation) && (
					<div className={styles.defaultInputContainer}>
						<Input
							key="Recomendación:"
							className={`${!isUpdateable ? styles.inputNoEditable : styles.input} ${disabledStyle ? styles.disabledStyle : ""}`}
							disabled={disabled}
							name={label}
							required={isRequired}
							label="Recomendación:"
							placeholder={placeholder || ""}
							icon=""
							value={recommendation}
							onChange={handleInputDefault}
							fluid
						/>
					</div>
				)}
			</>
		);
	};

	const defaultField = ({ label, isUpdateable, order, value, placeholder, isRequired, icon, disabled, type, maxDigits }) => {
		const inputType = includes(["date", "number"], type) ? type : "text";
		const searchOptions = getOptions();
		const [primaryValue = "", secondaryValue = ""] = value ? value.split(separatorSymbol) : ["", ""];
		const dropdownProps =
			searchOptions.length > 0
				? {
						dropdown: {
							disabled,
							options: searchOptions,
							name: "dropDownSeparateValue",
							onChange: handleInputDefault,
							value: secondaryValue,
						},
						dropdownPosition: "right",
				  }
				: {};

		return (
			<div className={`${styles.defaultInputContainer} ${!isUpdateable ? styles.fieldContainerDisabled : ""}`}>
				<Input
					key={`field-${label}-${order}`}
					className={`${!isUpdateable ? styles.inputNoEditable : styles.input} ${disabledStyle ? styles.disabledStyle : ""}`}
					disabled={disabled}
					name={label}
					required={isRequired}
					label={label}
					placeholder={placeholder || ""}
					icon={icon}
					value={primaryValue}
					onChange={(event, data) => {
						if (maxDigits && data.value && String(data.value).length > maxDigits) {
							return;
						}
						handleInputDefault(event, data);
					}}
					fluid
					type={inputType}
					dropdown={dropdownProps.dropdown}
					dropdownPosition={dropdownProps.dropdownPosition}
				/>
			</div>
		);
	};

	const errorRender = () => {
		const { error } = fieldData;
		if (error) {
			return (
				<Label basic color="red" className={styles.errorLabel}>
					{error}
				</Label>
			);
		}
		return null;
	};

	// field render options
	const companyField = () => {
		const { type, isUpdateable } = fieldData;
		const disabled = !isEditable || !isUpdateable;
		const currentFieldData = { ...fieldData, disabled };

		switch (type) {
			case "enum":
				return enumField(currentFieldData);

			case "group":
				return groupField(currentFieldData);

			case "label":
				return labelField(currentFieldData);

			case "note":
				return noteField(currentFieldData);

			case "bool":
				return boolField(currentFieldData);

			case "direction":
				return directionField(currentFieldData);

			case "image":
				return imageField(currentFieldData);

			case "formula":
				return formulaField(currentFieldData);

			default:
				return defaultField(currentFieldData);
		}
	};

	const handleSendMessage = () => {
		const text = questionsLanguage !== "ES" ? get(fieldData, "question2", "") : get(fieldData, "question", "");
		const currentChat = find(get(chats, "chats", []), { id: get(chats, "currentChatId") });
		const newMessage = messageBuilder({ chat: currentChat, reply, userName, text });
		dispatch(sendMessage({ message: newMessage }));
	};

	return (
		<>
			<div className={styles.fieldContainer}>
				<div style={{ width: "100%" }}> {companyField()} </div>
				{!isEmpty(get(fieldData, "question", "")) && isEditable && !hasValue() && !noSendQuestion && (
					<Button icon="send" className={styles.sendQuestionButton} onClick={handleSendMessage} />
				)}
			</div>

			{errorRender()}
		</>
	);
};

Field.propTypes = {
	fieldData: PropTypes.object,
	fieldCard: PropTypes.object,
	isEditable: PropTypes.bool,
	noSendQuestion: PropTypes.bool,
	disabledStyle: PropTypes.bool,
	companyFieldsInputHandler: PropTypes.func,
	dispatch: PropTypes.func,
	userName: PropTypes.string,
	questionsLanguage: PropTypes.string,
	reply: PropTypes.object,
	chats: PropTypes.object,
	groups: PropTypes.array,
	groupsToken: PropTypes.array,
	token: PropTypes.string,
	companyName: PropTypes.string,
};

function mapStateToProps({ user, chats, company }) {
	return {
		userName: user.name,
		reply: chats.reply,
		chats,
		groups: get(company, "groups", []),
		companyName: company.name,
	};
}
export default connect(mapStateToProps)(Field);
